"use client";

/**
 * useAdminForm — 폼 값 + 검증 + 제출 표시를 한 번에 관리하는 훅
 *
 * 필드마다 value/onChange/forceValidate 를 직접 만들 필요 없이 field() 가 묶어준다.
 * 저장 버튼 onClick 에서 form.validate() 한 줄로 전체 검증 + 오류 표시가 된다.
 *
 * @example
 * const form = useAdminForm({ name: "", email: "", password: "" });
 *
 * <AdminFormField label="이름" {...form.field("name", { required: true, type: "text" })} />
 * <AdminFormField label="이메일" {...form.field("email", { required: true, type: "email" })} />
 *
 * <Button
 *   onClick={() => {
 *     if (form.validate()) {     // 전체 검증 + 오류 표시
 *       save(form.values);       // 통과 시에만 저장
 *       form.reset();
 *     }
 *   }}
 * >
 *   저장
 * </Button>
 *
 * // 상세 조회 결과로 채우기: 없는/null 필드는 initial 값으로 자동 채워지므로
 * // 필드마다 `detail.x ?? ""` 를 나열할 필요가 없다.
 * useEffect(() => {
 *   if (open) form.reset(mode === "add" ? undefined : detail);
 * }, [open, detail]);
 */
import { useRef, useState } from "react";

import {
  type FieldRule,
  getFieldError,
} from "./admin-form-field";

export function useAdminForm<T extends Record<string, unknown>>(
  initial: { [K in keyof T]: string }
) {
  // 폼이 실제로 다루는 값의 타입 — T 는 필드 이름(keyof)만 빌려오고,
  // 값 자체는 항상 string 으로 고정한다. T 의 필드가 `string | null` 이어도
  // (API 원본 타입을 그대로 Pick 해서 쓰는 경우가 많다) initial 을 문자열로만
  // 채우면 그대로 통과한다 — 컨트롤드 input 은 어차피 항상 문자열이기 때문.
  type Values = { [K in keyof T]: string };

  const [values, setValues] = useState<Values>(initial);
  const [submitted, setSubmitted] = useState(false);
  // 각 필드의 검증 규칙 (field() 호출 시 등록)
  const rules = useRef<Partial<Record<keyof T, FieldRule>>>({});
  // field() 가 호출된 순서 (등록된 순서 = 화면에 나열된 순서로 간주)
  const fieldOrder = useRef<(keyof T)[]>([]);
  // validate() 시점에 "가장 먼저 있는 오류 필드"만 포커싱하도록 지정
  const [focusTarget, setFocusTarget] = useState<keyof T | null>(null);
  // 같은 필드가 여전히 invalid 여도 재제출 시 다시 포커싱되도록 매 validate() 마다 증가
  const [focusToken, setFocusToken] = useState(0);

  /** AdminFormField 에 스프레드할 props 를 만든다. */
  const field = (name: keyof T, rule: FieldRule = {}) => {
    rules.current[name] = rule;
    if (!fieldOrder.current.includes(name)) fieldOrder.current.push(name);
    return {
      value: values[name],
      onChange: (v: string) =>
        setValues((s) => ({ ...s, [name]: v }) as Values),
      type: rule.type,
      required: rule.required,
      validate: rule.validate,
      forceValidate: submitted,
      autoFocusOnError: focusTarget === name,
      focusToken,
    };
  };

  /** 특정 필드의 현재 오류 (없으면 undefined) */
  const errorOf = (name: keyof T) =>
    getFieldError(rules.current[name] ?? {}, values[name]);

  /** 전체 검증 + 오류 표시. 모두 통과하면 true. 가장 먼저 있는 오류 필드로 포커스를 옮긴다. */
  const validate = () => {
    setSubmitted(true);
    const firstInvalid = fieldOrder.current.find((name) => !!errorOf(name));
    setFocusTarget(firstInvalid ?? null);
    setFocusToken((t) => t + 1);
    return firstInvalid === undefined;
  };

  /**
   * 값/제출 상태 초기화.
   * source 를 넘기면 initial 의 각 필드를 기준으로, source 에 값이 있는 필드만 덮어써서 리셋한다
   * (undefined/null 인 필드는 initial 값 그대로 유지 — 필드별 `?? ""` 나열이 필요 없다).
   */
  const reset = (source?: Partial<Record<keyof T, unknown>> | null) => {
    if (!source) {
      setValues(initial);
    } else {
      const next = { ...initial };
      (Object.keys(initial) as (keyof T)[]).forEach((key) => {
        const v = source[key];
        if (v !== undefined && v !== null) {
          // object/array 필드(예: address_json)는 String(v) 하면 "[object Object]"가
          // 되어버리므로 JSON.stringify 로 직렬화한다 — select 의 number 필드처럼
          // 원시값은 그대로 String() 으로 문자열화한다.
          next[key] = (
            typeof v === "object" ? JSON.stringify(v) : String(v)
          ) as Values[keyof T];
        }
      });
      setValues(next);
    }
    setSubmitted(false);
    setFocusTarget(null);
  };

  return { values, setValues, submitted, setSubmitted, field, errorOf, validate, reset };
}

/** useAdminForm() 반환 타입. 단계별로 나뉜 폼 하위 컴포넌트에 form 을 props 로 넘길 때 사용한다. */
export type UseAdminFormReturn<T extends Record<string, unknown>> = ReturnType<
  typeof useAdminForm<T>
>;
